import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Layout, PageHeader, Row, Col, message } from 'antd'
import SearchBar from './SearchBar'
import List from './List'
import StoDuplicatesWidget from '@components/StoDuplicatesWidget'
import { tranzila } from '@services/api'
import { pruneStoSearchHandoffs } from '@utils/stoSearchHandoff'
import {
    CATEGORIES,
    CLIENT_SEARCH_FILTER_KEY_BY_PARAM,
    DEFAULT_TERMINALS,
    DEFAULT_CATEGORIES,
    emptyTransactionFilters,
    initialTransactionDateRange,
    inactiveTransactionFilters,
    matchesCategory,
    matchesTransactionErrors,
    matchesTransactionFilters,
    rowsForCategories,
    transactionErrorOptions,
    transactionFiltersForRows
} from './utils'
import moment from 'moment'

const hasOwn = (value, key) => !!value && Object.prototype.hasOwnProperty.call(value, key)
const FAILED_CHARGE_GROUPS = ['card_failed', 'bank_failed']
const SUCCESS_CHARGE_GROUPS = ['card_success', 'bank_success']
const TRANZILA_EXPORT_TOO_MANY_ROWS = 'TRANZILA_TRANSACTIONS_FILE_TOO_MANY_ROWS'
const TRANZILA_EXPORT_MAX_ROWS = 10000
const sortTransactions = list =>
    [...list].sort((a, b) =>
        `${b.transaction_date} ${b.transaction_time}`.localeCompare(`${a.transaction_date} ${a.transaction_time}`)
    )
const emptyResultMetadata = () => ({
    list: [],
    counts: {},
    billingCounts: {},
    responseTotal: 0,
    filterDefinitions: [],
    errorCodes: [],
    page: 1
})
const emptyResultsState = () => ({
    ...emptyResultMetadata(),
    ...emptyTransactionFilters()
})

@inject((store: any) => {
    const { synonymsStore } = store
    return { synonymsStore }
})
@observer
class Tranzila extends Component<any, any> {
    initialDateRange = initialTransactionDateRange()
    excelExportRequest = false
    dataRequestId = 0

    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            list: [],
            counts: {},
            billingCounts: {},
            responseTotal: 0,
            terminals: [...DEFAULT_TERMINALS],
            categories: [...DEFAULT_CATEGORIES],
            errorCodes: [],
            filterDefinitions: [],
            clientSearchMode: false,
            clientSearchSummary: null,
            excelExportLoading: false,
            page: 1,
            ...emptyTransactionFilters()
        }
    }
    fetchData = async ({ Fdate, Tdate, terminals }) => {
        if (!terminals || !terminals.length) {
            message.error('יש לבחור לפחות טרמינל אחד')
            this.setState({ page: 1 })
            return
        }
        const requestId = ++this.dataRequestId
        this.setState({ loading: true, page: 1 })
        try {
            const res = await tranzila.getTranzilaData({
                terminals: terminals.join(','),
                Fdate: moment(new Date(Fdate)).format('YYYY-MM-DD'),
                Tdate: moment(new Date(Tdate)).format('YYYY-MM-DD')
            })
            const payload = res && res.data
            const list = Array.isArray(payload) ? payload : (payload && payload.transactions) || []
            const counts = (payload && payload.counts) || {}
            const billingCounts = (payload && payload.billing_counts) || {}
            const responseTotal = (payload && payload.total) || list.length
            const filterDefinitions = transactionFiltersForRows(list)
            if (requestId !== this.dataRequestId) return
            this.reportBillingIntegrity(list, counts, billingCounts)
            const sorted = sortTransactions(list)
            this.setState({
                list: sorted,
                counts,
                billingCounts,
                responseTotal,
                terminals,
                filterDefinitions,
                errorCodes: [],
                page: 1,
                ...inactiveTransactionFilters(filterDefinitions)
            })
        } catch (e) {
            if (requestId !== this.dataRequestId) return
            console.log(e && e.response && e.response.data)
            const responseStatus = e && e.response && e.response.status
            const allTerminalsSelected =
                terminals.length === DEFAULT_TERMINALS.length &&
                DEFAULT_TERMINALS.every(terminal => terminals.includes(terminal))
            if (responseStatus === 400 && allTerminalsSelected) {
                message.error('טווח התאריכים רחב מדי לחיפוש בכל הטרמינלים. יש לבחור טווח קצר יותר')
            } else {
                message.error('שגיאה בטעינת עסקאות טרנזילה')
            }
        } finally {
            if (requestId === this.dataRequestId) this.setState({ loading: false })
        }
    }
    fetchDefaultData = () =>
        this.fetchData({ ...initialTransactionDateRange(), terminals: [...DEFAULT_TERMINALS] })
    componentDidMount = () => {
        pruneStoSearchHandoffs()
        this.fetchDefaultData()
    }

    handleSubmit = (Fdate, Tdate, terminals) => this.fetchData({ Fdate, Tdate, terminals })
    setDisplayState = values => this.setState({ ...values, page: 1 })
    setFilter = (name, value) => this.setDisplayState({ [name]: value })
    // error options live inside the selected category, so when it changes the selected code may
    // disappear from the list. Keeping it would filter the table down to zero with no visible reason.
    setCategories = categories => {
        const available = transactionErrorOptions(rowsForCategories(this.state.list, categories)).map(
            option => option.value
        )
        this.setDisplayState({
            categories,
            errorCodes: this.state.errorCodes.filter(code => available.includes(code))
        })
    }
    setErrorCodes = errorCodes => this.setDisplayState({ errorCodes })
    setTerminals = terminals => this.setDisplayState({ terminals })
    resetPage = () => this.setState({ page: 1 })
    setPage = page => this.setState({ page })
    clearClientSearchResults = (resetRefinements = false) => {
        this.dataRequestId += 1
        this.setState({
            ...emptyResultMetadata(),
            ...(resetRefinements ? { categories: [], ...emptyTransactionFilters() } : {}),
            loading: false,
            clientSearchSummary: null
        })
    }
    setClientSearchMode = clientSearchMode => {
        this.dataRequestId += 1
        this.setState(
            {
                ...emptyResultsState(),
                loading: false,
                clientSearchMode,
                clientSearchSummary: null,
                terminals: [...DEFAULT_TERMINALS],
                categories: clientSearchMode ? [] : [...DEFAULT_CATEGORIES],
                errorCodes: []
            },
            () => {
                if (!clientSearchMode) this.fetchDefaultData()
            }
        )
    }
    searchClientTransactions = async ({ param, label, value, from, to, terminals }) => {
        const requestId = ++this.dataRequestId
        const params: any = { param, value }
        if (from) params.from = from
        if (to) params.to = to

        this.setState({ ...emptyResultMetadata(), loading: true, clientSearchSummary: null })
        try {
            const res = await tranzila.searchTranzilaTransactions(params)
            const payload = res && res.data
            const list = Array.isArray(payload) ? payload : (payload && payload.transactions) || []
            const counts = (payload && payload.counts) || {}
            const billingCounts = (payload && payload.billing_counts) || {}
            const responseTotal = (payload && payload.total) || list.length
            const searchedTerminals =
                payload && Array.isArray(payload.terminals) && payload.terminals.length ? payload.terminals : terminals
            const supersededFilterKey = CLIENT_SEARCH_FILTER_KEY_BY_PARAM[param]
            const filterDefinitions = transactionFiltersForRows(list).filter(
                filter => filter.key !== supersededFilterKey
            )

            if (requestId !== this.dataRequestId) return
            this.setState({
                list: sortTransactions(list),
                counts,
                billingCounts,
                responseTotal,
                filterDefinitions,
                errorCodes: [],
                page: 1,
                clientSearchSummary: { param, label, value, from, to, terminals: searchedTerminals },
                ...inactiveTransactionFilters(filterDefinitions)
            })
        } catch (e) {
            if (requestId !== this.dataRequestId) return
            console.log(e && e.response && e.response.data)
            message.error('שגיאה בביצוע חיפוש ממוקד')
        } finally {
            if (requestId === this.dataRequestId) this.setState({ loading: false })
        }
    }
    downloadExcel = async (rows, meta) => {
        if (this.excelExportRequest || !rows.length) return

        this.excelExportRequest = true
        this.setState({ excelExportLoading: true })
        try {
            await tranzila.downloadTransactionsFile({ rows, meta })
        } catch (e) {
            const status = e && e.response && e.response.status
            const payload = await tranzila.responseErrorPayload(e)
            const errorCode = payload && (payload.code || payload.err_key)
            if (status === 400 && errorCode === TRANZILA_EXPORT_TOO_MANY_ROWS) {
                const maxRows = Number(payload.max_rows) || TRANZILA_EXPORT_MAX_ROWS
                const narrowingHint = this.state.clientSearchMode
                    ? 'יש לצמצם עוד באמצעות קטגוריות, מסננים או טווח התאריכים ולנסות שוב'
                    : 'יש לצמצם את טווח התאריכים או את בחירת הטרמינלים ולנסות שוב'
                message.error(`ניתן לייצא עד ${maxRows.toLocaleString('he-IL')} עסקאות. ${narrowingHint}`)
            } else {
                message.error('ייצוא העסקאות לאקסל נכשל')
            }
        } finally {
            this.excelExportRequest = false
            this.setState({ excelExportLoading: false })
        }
    }
    reportBillingIntegrity = (list, counts, billingCounts) => {
        const failedCharges = list.filter(transaction => FAILED_CHARGE_GROUPS.includes(transaction.group))
        const issues = []
        const checkSplit = ({ rows, recurringKey, oneoffKey, label, groupCountKeys }) => {
            const recurringCategory = CATEGORIES.find(category => category.key === recurringKey)
            const oneoffCategory = CATEGORIES.find(category => category.key === oneoffKey)
            const recurring = rows.filter(transaction => matchesCategory(transaction, recurringCategory))
            const oneoff = rows.filter(transaction => matchesCategory(transaction, oneoffCategory))
            const classified = recurring.length + oneoff.length
            const unclassified = rows.filter(
                transaction => !recurring.includes(transaction) && !oneoff.includes(transaction)
            )

            if (unclassified.length) {
                const rowRefs = unclassified
                    .slice(0, 10)
                    .map(
                        transaction =>
                            `${transaction.index || transaction.txnfdid || '?'} (group=${transaction.group ||
                                'null'}, billing_kind=${transaction.billing_kind || 'null'})`
                    )
                    .join(', ')
                const remainder = unclassified.length > 10 ? ` +${unclassified.length - 10}` : ''
                issues.push(`${label}: עסקאות שלא שויכו לפיצול: ${rowRefs}${remainder}`)
            }
            if (classified !== rows.length) {
                issues.push(`${label}: הפיצול הוא ${classified} מתוך ${rows.length}`)
            }
            if (hasOwn(billingCounts, recurringKey) && recurring.length !== Number(billingCounts[recurringKey])) {
                issues.push(
                    `${recurringKey}: התקבלו ${recurring.length} שורות מול ${billingCounts[recurringKey]} בסיכום`
                )
            }
            if (hasOwn(billingCounts, oneoffKey) && oneoff.length !== Number(billingCounts[oneoffKey])) {
                issues.push(`${oneoffKey}: התקבלו ${oneoff.length} שורות מול ${billingCounts[oneoffKey]} בסיכום`)
            }
            if (
                groupCountKeys.every(key => hasOwn(counts, key)) &&
                hasOwn(billingCounts, recurringKey) &&
                hasOwn(billingCounts, oneoffKey)
            ) {
                const oldCount = groupCountKeys.reduce((sum, key) => sum + Number(counts[key]), 0)
                const splitCount = Number(billingCounts[recurringKey]) + Number(billingCounts[oneoffKey])
                if (oldCount !== splitCount) {
                    issues.push(`${label}: סכומי השרת אינם תואמים: ${oldCount} ישן מול ${splitCount} מפוצל`)
                }
            }
        }

        checkSplit({
            rows: failedCharges,
            recurringKey: 'recurring_failed',
            oneoffKey: 'oneoff_failed',
            label: 'חיובים שנכשלו',
            groupCountKeys: FAILED_CHARGE_GROUPS
        })
        checkSplit({
            rows: list.filter(transaction => SUCCESS_CHARGE_GROUPS.includes(transaction.group)),
            recurringKey: 'recurring_success',
            oneoffKey: 'oneoff_success',
            label: 'חיובים שהצליחו',
            groupCountKeys: SUCCESS_CHARGE_GROUPS
        })
        if (issues.length) {
            console.warn('Tranzila billing_kind integrity issue', { issues })
            message.warning(`פער בסיווג חיובים: ${issues.join(' · ')}`, 12)
        }
    }
    handleReset = (Fdate, Tdate) => {
        const terminals = [...DEFAULT_TERMINALS]
        this.setDisplayState({
            terminals,
            categories: [...DEFAULT_CATEGORIES],
            errorCodes: [],
            ...emptyTransactionFilters()
        })
        this.fetchData({ Fdate, Tdate, terminals })
    }

    render() {
        const {
            loading,
            list,
            counts,
            billingCounts,
            responseTotal,
            terminals,
            categories,
            errorCodes,
            filterDefinitions,
            clientSearchMode,
            clientSearchSummary,
            excelExportLoading,
            page
        } = this.state
        const selectedCategories = CATEGORIES.filter(category => categories.includes(category.key))
        const byCategory = rowsForCategories(list, categories)
        const byError = byCategory.filter(transaction => matchesTransactionErrors(transaction, errorCodes))
        const displayedTransactions = byError.filter(t =>
            matchesTransactionFilters(t, filterDefinitions, this.state)
        )
        const errorOptions = transactionErrorOptions(byCategory)
        const categoryTitle = selectedCategories.length
            ? selectedCategories.map(category => category.label).join(' + ')
            : 'הכל'
        return (
            <Layout style={{ padding: '0 1rem' }}>
                <PageHeader
                    className="tranzila-header"
                    onBack={() => window.history.back()}
                    title="עסקאות טרנזילה"
                    subTitle={
                        clientSearchMode
                            ? clientSearchSummary
                                ? `חיפוש ממוקד — נמצאו ${displayedTransactions.length} עסקאות עבור ${clientSearchSummary.label}: ${clientSearchSummary.value}`
                                : 'חיפוש ממוקד — טרם בוצע חיפוש'
                            : `${categoryTitle} (נמצאו ${displayedTransactions.length} עסקאות)`
                    }
                    extra={<StoDuplicatesWidget />}
                />
                <Row gutter={10} style={{ padding: '0 2rem' }}>
                    <Col>
                        <SearchBar
                            counts={counts}
                            billingCounts={billingCounts}
                            responseTotal={responseTotal}
                            categories={categories}
                            setCategories={this.setCategories}
                            errorCodes={errorCodes}
                            errorOptions={errorOptions}
                            setErrorCodes={this.setErrorCodes}
                            defaultTerminals={terminals}
                            defaultFdate={this.initialDateRange.Fdate}
                            defaultTdate={this.initialDateRange.Tdate}
                            setTerminals={this.setTerminals}
                            resetPage={this.resetPage}
                            clientSearchMode={clientSearchMode}
                            setClientSearchMode={this.setClientSearchMode}
                            handleClientSearch={this.searchClientTransactions}
                            clearClientSearchResults={this.clearClientSearchResults}
                            handleSubmit={this.handleSubmit}
                            handleReset={this.handleReset}
                            handleExport={meta => this.downloadExcel(displayedTransactions, meta)}
                            exportRowsCount={displayedTransactions.length}
                            exportLoading={excelExportLoading}
                            filterDefinitions={filterDefinitions}
                            filters={this.state}
                            setFilter={this.setFilter}
                        />
                    </Col>
                </Row>
                <Row gutter={10} style={{ padding: '0 2rem' }}>
                    <Col>
                        <List list={displayedTransactions} loading={loading} page={page} setPage={this.setPage} />
                    </Col>
                </Row>
            </Layout>
        )
    }
}

export default withRouter(Tranzila)
